/**
 * Leave Ritual Confirmation Modal
 * Modal to confirm leaving a ritual, following LogoutConfirmationModal pattern
 */

import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/language"

interface LeaveRitualModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading?: boolean
  ritualTitle?: string
  isDelete?: boolean
}

export function LeaveRitualModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  ritualTitle,
  isDelete = false,
}: LeaveRitualModalProps) {
  const { t } = useTranslation("chats")

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent
        showCloseButton={false}
        overlayClass="bg-[#020202E5]"
        className="md:w-[520px] bg-[#020202] border border-[#F2F0EB4D] text-[#F2F0EB] pt-12.5 pb-10 px-12.5 gap-7.5"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 text-[#F2F0EB] bg-[#F2F0EB0D] border border-[#F2F0EB26] p-2.5 rounded-full transition-opacity hover:opacity-100 focus:outline-none"
        >
          <X className="h-4 w-4" />
        </button>
        <DialogHeader className="text-center gap-2.5">
          <DialogTitle className="text-[#F2F0EBE6] text-xl font-semibold text-center leading-6">
            {isDelete ? t("deleteRitualModal.title") : t("leaveRitualModal.title")}
          </DialogTitle>
          <DialogDescription className="text-[#F2F0EBE6] text-center text-sm leading-4.25">
            {isDelete ? (
              t("deleteRitualModal.description", { title: ritualTitle })
            ) : ritualTitle ? (
              <>
                {t("leaveRitualModal.descriptionWithTitle", { title: ritualTitle })}
                <br />
                {t("leaveRitualModal.descriptionWithTitleRejoin")}
              </>
            ) : (
              <>
                {t("leaveRitualModal.description")}
                <br />
                {t("leaveRitualModal.descriptionRejoin")}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-row gap-2.5 sm:flex-row">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 h-[50px] md:w-[205px] py-3.75 uppercase"
          >
            {isDelete ? t("deleteRitualModal.cancel") : t("leaveRitualModal.cancel")}
          </Button>
          <Button
            variant="primary-terracotta"
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 h-[50px] md:w-[205px] py-3.75 uppercase"
          >
            {isLoading
              ? isDelete
                ? t("deleteRitualModal.deleting")
                : t("leaveRitualModal.leaving")
              : isDelete
                ? t("deleteRitualModal.delete")
                : t("leaveRitualModal.leave")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
